// UsageMeter.jsx — what's left, in the top-right corner of the website.
//
// Braeden 2026-09-06: "we need to add a credit tracker, or usage tracker, for
// people using it for free ... if they are on a free plan they have like $5 of
// free credits a week so limit it to $1.5 a day, and if they are members they
// have $20 with access to lots of cool stuff obviously like assist mode and
// downloads"
//
// ── IT SAYS DOLLARS, NEVER "CREDITS" ──────────────────────────────────────
// ARIA_CONTEXT's money rule: she talks about money dollar-first and never says
// "credits" or "tokens". The file is named for what he asked for; every word
// on screen is a dollar amount and a time.
//
// ── ONE NUMBER, AND IT IS THE ONE THAT STOPS YOU ──────────────────────────
// A free person has two pots — $1.50 today out of $5.00 this week — so there
// are two numbers and only one of them matters at any moment. The pill shows
// the binding one ("$1.12 left today"), and the panel shows both, because a
// meter that shows the roomier pot is a meter that lets you walk into a wall
// it was watching. The backend decides which one binds (creditsService
// getMeter → `binding`); nothing here does arithmetic on a plan.
//
// ── A MEMBER IS NOT SHOWN A CAGE ──────────────────────────────────────────
// Free sees a bar filling up. A member sees what they bought — assist mode,
// the download — with the month's balance underneath as information, not as a
// warning. Same component, because they are the same fact about the account.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBase } from './apiBase';
import PlanPopup from './PlanPopup';

const POLL_MS = 45000;

function readUserId() {
  try { return localStorage.getItem('aria_user_id') || ''; } catch (_) { return ''; }
}

/**
 * How full the tank is, 0-100 — of whichever pot is the one actually stopping
 * you (the backend decides that in `binding`). null when there is nothing to
 * measure: an unmetered account, or no id yet.
 *
 * A pot with real money in it never rounds down to 0%: "0%" has to mean spent,
 * or the battery is lying about the one state it exists to show.
 */
function pillPercent(m) {
  if (!m || m.unlimited || m.tracking === false) return null;
  const left = m.remainingCents;
  if (left === null || left === undefined) return null;
  const pot = (m.binding === 'day' && m.day && m.day.capCents)
    ? m.day.capCents
    : (m.period && m.period.grantCents);
  if (!pot) return null;
  if (left <= 0) return 0;
  return Math.max(1, Math.min(100, Math.round((left / pot) * 100)));
}

/**
 * Horizontal battery — shell, terminal cap on the right, and a fill that
 * empties right-to-left (Braeden 9/6: "change the charger looking thing so its
 * horizantal and looks like a real battery charger symble with no money
 * instead it just needs to be a percentage"). The bolt replaces the fill on an
 * unmetered account, which is exactly what a charging battery means.
 */
function Battery({ pct, charging }) {
  const INNER_X = 2.4;
  const INNER_W = 17.4;
  // Charging shows the LEVEL and the bolt, the way a phone does — a full
  // battery with a bolt on a nearly-empty account would be the one lie this
  // component exists to prevent. pct null (unmetered) is the only full fill.
  const w = pct === null || pct === undefined
    ? INNER_W
    : Math.max(0, (INNER_W * Math.max(0, Math.min(100, pct))) / 100);
  return (
    <svg className="usage-battery" viewBox="0 0 26 13" aria-hidden="true" focusable="false">
      <rect className="usage-battery__shell" x="0.8" y="0.8" width="21.4" height="11.4" rx="3.2" />
      <path className="usage-battery__cap" d="M23.4 4.5c1.4 0 2 .6 2 2s-.6 2-2 2z" />
      {w > 0 && <rect className="usage-battery__fill" x={INNER_X} y="2.5" width={w} height="8" rx="1.7" />}
      {charging && <path className="usage-battery__bolt" d="M12.9 3.1l-3.6 4.6h2.6l-.8 3.2 3.6-4.6h-2.6z" />}
    </svg>
  );
}

export default function UsageMeter({ hidden = false, ballLive = false, busy = false, guestRemaining = null }) {
  const [meter, setMeter] = useState(null);
  const [open, setOpen] = useState(false);
  // "Get more usage" opens the REAL plan chooser — the same cards, chrome and
  // CSS as signup's (PlanPopup + planCatalog) — instead of an in-panel list of
  // its own. Braeden 9/6: "it needs to pop up the normal both options to
  // choose from, like the plans, dont we have a pop up for that already".
  const [plansOpen, setPlansOpen] = useState(false);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const wasBusyRef = useRef(false);

  const load = useCallback(async () => {
    const userId = readUserId();
    try {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const t = setTimeout(() => { try { ac.abort(); } catch (_) {} }, 6000);
      const res = await fetch(
        `${getApiBase()}/api/subscription/meter${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`,
        { signal: ac.signal }
      );
      clearTimeout(t);
      if (!res.ok) return;
      const json = await res.json();
      if (json && json.ok) setMeter(json);
    } catch (_) {
      // A meter that throws must never take the page with it. Keep the last
      // good reading on screen rather than flashing an error at someone who
      // was only ever going to glance at it.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => {
      // Don't poll a tab nobody is looking at.
      if (typeof document === 'undefined' || document.visibilityState === 'visible') load();
    }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    // Anything that spends money can nudge this and the corner updates without
    // waiting out the poll: window.dispatchEvent(new Event('aria-usage-refresh'))
    const onNudge = () => load();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('aria-usage-refresh', onNudge);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('aria-usage-refresh', onNudge);
      try { if (abortRef.current) abortRef.current.abort(); } catch (_) {}
    };
  }, [load]);

  // A turn just finished — read the balance again instead of leaving a stale
  // number up for the rest of the poll. `busy` is the app's own `loading`, so
  // this hooks the ONE place every path already funnels through rather than
  // chasing the dozen sites that can end a turn. Anything else that spends can
  // announce itself with window.dispatchEvent(new Event('aria-usage-refresh')).
  // The wait is not politeness. The backend charges the turn AFTER the reply
  // is on the wire and deliberately does not await the ledger write, so the
  // reply is never held up by billing — which means a refresh fired the
  // instant the turn ends reads the balance from BEFORE the turn. Measured:
  // the ledger went to $1.49 and the corner still said $1.50 until the next
  // poll. One beat, and the number is the one the turn produced.
  useEffect(() => {
    if (!wasBusyRef.current || busy) { wasBusyRef.current = busy; return undefined; }
    wasBusyRef.current = busy;
    const t = setTimeout(load, 1200);
    return () => clearTimeout(t);
  }, [busy, load]);

  // The circle wins, always. Braeden 8/16: "if im talking to the ai and the
  // mic is open than this always needs to be displayed over everything so i
  // can always see it" — so when the ball goes live it leaves the corner dock
  // and grows to 210px across the space this panel occupies. Rather than
  // dodging a moving 210px circle, the panel steps aside the moment she starts
  // listening, thinking or talking. Nothing is lost: the pill still reads.
  useEffect(() => { if (ballLive) setOpen(false); }, [ballLive]);



  // Click-away and Escape close the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (hidden || (!meter && guestRemaining === null)) return null;

  if (!meter && guestRemaining !== null) {
    const pct = Math.max(0, Math.min(100, Math.round((guestRemaining / 12) * 100)));
    return (
      <div className={`usage-meter usage-meter--${pct <= 20 ? 'out' : pct <= 50 ? 'low' : 'normal'}`} ref={wrapRef}>
        <button type="button" className="usage-meter__pill" aria-label={`${guestRemaining} free conversations left. Open usage details.`} title={`${guestRemaining} free conversations left`}>
          <Battery pct={pct} charging={false} /><span className="usage-meter__text">{pct}%</span>
        </button>
      </div>
    );
  }

  const pct = pillPercent(meter);
  const charging = !!meter.unlimited;
  // Nothing to show: no id yet, or a shape we can't read. Better an empty
  // corner than a battery reporting a number it made up.
  if (pct === null && !charging) return null;

  // ── GREEN / YELLOW / RED IS THE POWER MODE, NOT JUST THE LEVEL ────────
  // Braeden 9/6: green = full power, yellow = save power (cheaper, less
  // powerful), red = at or under 20% where save power switches itself on the
  // way a phone does — unless extra usage is on, which turns the battery into
  // a CHARGING one, green or yellow depending on the mode.
  // The backend decides all of it (creditsService.getMeter → power), so the
  // colour, the model and the voice can never disagree.
  const power = meter.power || { color: 'green', effective: 'full', charging: false, canExtraUsage: false, auto: false, thresholdPct: 20 };
  const tone = charging ? 'normal' : power.color === 'red' ? 'out' : power.color === 'yellow' ? 'low' : 'normal';
  const bolt = !!power.charging;

  // Spoken/hover form keeps the detail the number drops: which pot, and when
  // it refills. The pill itself is just the battery and the percentage.
  const modeWord = power.effective === 'save' ? 'save power' : 'full power';
  const detail = charging
    ? `${meter.planLabel} — not metered`
    : `${meter.planLabel} — ${pct}% left, ${modeWord}`
      + (bolt ? ' · extra usage on' : '')
      + (power.auto ? ` · save power came on at ${power.thresholdPct}%` : '')
      + (pct <= 0 && !bolt ? ` · back ${meter.binding === 'day' ? 'tomorrow' : (meter.periodLabel === 'month' ? 'next month' : 'Monday')}` : '');

  // The popup owns what happens next (buy / sign up / close); the pill only
  // has to get out of the way and open it.
  const openPlans = () => { setOpen(false); setPlansOpen(true); };

  // Both switches are server-side truth, not browser flags — the model picker
  // and the voice read them, and a billing choice kept only in a tab is the
  // old broken behaviour routes/userBillingPrefs.js was written to fix. The
  // reply carries the new meter, so the battery recolours from the same source
  // that decided it.
  const setPower = async (mode) => {
    const userId = readUserId();
    if (!userId) return;
    try {
      const r = await fetch(`${getApiBase()}/api/subscription/power`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, mode }),
      });
      const j = await r.json();
      if (j && j.meter) setMeter(j.meter); else load();
    } catch (_) { load(); }
  };

  const setExtraUsage = async (allow) => {
    const userId = readUserId();
    if (!userId) return;
    try {
      await fetch(`${getApiBase()}/api/user/billing/overage`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, allow: !!allow }),
      });
    } catch (_) {}
    load();
  };

  return (
    <div className={`usage-meter usage-meter--${tone}${bolt ? ' usage-meter--charging' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`usage-meter__pill${meter.member ? ' usage-meter__pill--member' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${detail}. Open usage details.`}
        title={detail}
      >
        <Battery pct={pct} charging={charging || bolt} />
        {!charging && <span className="usage-meter__text">{pct}%</span>}
      </button>

      {open && (
        <div className="usage-meter__panel" role="group" aria-label="Usage">
          {/* ── TWO SWITCHES, NOTHING ELSE (Braeden 9/6) ────────────────────
              "get rid of the today and this week usage sense we already have
               the percentage, the only thing that should show up is a switch
               that says extra usage, and a iphone button that says save power
               mode so when they click on it, its in power save mode"

              The battery already answers "how much is left" — repeating it as
              two dollar rows underneath was the same fact three times. What is
              left is what a phone puts in Settings: the two switches that
              change how the thing behaves. "Get more usage" stays because it
              is the door to the plans, not a reading of the meter. */}
          <div className="usage-meter__panel-head">
            <span className="usage-meter__plan">{meter.planLabel}</span>
            {meter.member && !meter.unlimited ? <span className="usage-meter__price">$20 / month</span> : null}
            {meter.unlimited ? <span className="usage-meter__price">no limit</span> : null}
          </div>

          {meter.unlimited ? (
            <p className="usage-meter__note">
              This account isn&apos;t metered — nothing here counts against you.
            </p>
          ) : (
            <div className="usage-power">
              {/* Extra usage. Shown to everyone so the membership perk is
                  visible, but only a card on file can switch it on — for a
                  free account "keep spending past the cap" is a bill nobody
                  agreed to. */}
              <label className={`usage-row-switch${power.canExtraUsage ? '' : ' usage-row-switch--off'}`}>
                <span className="usage-row-switch__label">
                  Extra usage
                  <em>
                    {!power.canExtraUsage
                      ? 'Members only'
                      : power.extraUsage ? 'Keeps going past the cap' : 'Stops at the cap'}
                  </em>
                </span>
                <span className={`usage-switch${power.extraUsage ? ' on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!power.extraUsage}
                    disabled={!power.canExtraUsage}
                    onChange={(e) => setExtraUsage(e.target.checked)}
                  />
                  <span className="usage-switch__thumb" />
                </span>
              </label>

              {/* Save power mode. Below the line it is ON and cannot be turned
                  off — Braeden's rule is that 20% puts you there "unless they
                  turn on extra usage", so extra usage is the only way out and
                  the row says so instead of letting a switch snap back. */}
              <label className={`usage-row-switch${power.auto ? ' usage-row-switch--locked' : ''}`}>
                <span className="usage-row-switch__label">
                  Save power mode
                  <em>
                    {power.auto
                      ? `On by itself at ${power.thresholdPct}% — turn on extra usage to lift it`
                      : power.effective === 'save'
                        ? 'Lighter answers, free voice. Lasts longer.'
                        : 'Best model and voice. Uses your balance faster.'}
                  </em>
                </span>
                <span className={`usage-switch${power.effective === 'save' ? ' on' : ''}${power.auto ? ' usage-switch--locked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={power.effective === 'save'}
                    disabled={!!power.auto}
                    onChange={(e) => setPower(e.target.checked ? 'save' : 'full')}
                  />
                  <span className="usage-switch__thumb" />
                </span>
              </label>
            </div>
          )}

          {meter.upgrade ? (
            <button
              type="button"
              className="usage-meter__btn usage-meter__btn--go usage-meter__btn--more"
              onClick={openPlans}
            >
              Get more usage
            </button>
          ) : null}
        </div>
      )}

      {plansOpen && (
        <PlanPopup
          onClose={() => setPlansOpen(false)}
          currentKey={meter.member ? 'pro' : 'free'}
          signedUp={meter.signedUp !== false}
        />
      )}
    </div>
  );
}
